import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  TreeMessage,
  TreeIndex,
  VersionInfo,
  buildTreeIndex,
  computeActivePath,
  getDefaultBranchSelections,
  getVersionInfoForPathMessage,
} from '@/lib/conversation-tree';

/**
 * React hook that wraps the conversation tree logic.
 * Takes a flat messages array and returns the active linear path
 * plus functions to navigate between branches/versions.
 */
export function useConversationTree(messages: TreeMessage[]) {
  const [selectedBranches, setSelectedBranches] = useState<Record<string, string>>({});

  // Build tree index (recomputes when messages change)
  const treeIndex = useMemo(() => buildTreeIndex(messages), [messages]);

  // Merge default selections with existing ones when tree changes
  useEffect(() => {
    const defaults = getDefaultBranchSelections(treeIndex);
    setSelectedBranches(prev => {
      // Keep existing selections that are still valid in the new tree
      const merged: Record<string, string> = { ...defaults };
      for (const [parentId, childId] of Object.entries(prev)) {
        if (treeIndex.byId.has(childId) && (treeIndex.byId.has(parentId) || parentId === "__root__")) {
          merged[parentId] = childId;
        }
      }
      return merged;
    });
  }, [treeIndex]);

  // Compute the active display path
  const activePath = useMemo(
    () => computeActivePath(treeIndex, selectedBranches),
    [treeIndex, selectedBranches]
  );

  // Select a specific branch at a fork point
  const selectBranch = useCallback((parentId: string, childId: string) => {
    setSelectedBranches(prev => ({ ...prev, [parentId]: childId }));
  }, []);

  // Get version info for a message in the active path
  const getVersionInfoForMessage = useCallback(
    (message: TreeMessage): VersionInfo => {
      return getVersionInfoForPathMessage(treeIndex, message);
    },
    [treeIndex]
  );

  return {
    activePath,
    treeIndex,
    selectedBranches,
    selectBranch,
    getVersionInfoForMessage,
  };
}
